import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../assets/images/arka-saydam-logo.png';
import { API_BASE_URL } from '../config';
import { getClientInstallId } from '../utils/clientInstallId';

// Prop'lar: isDarkMode ve toggleDarkMode, App.js'ten gelmelidir.
const SignupPage = ({ isDarkMode, toggleDarkMode }) => {
    // Yönlendirme Hook'u
    const navigate = useNavigate();

    // Input Görüntüleme State'leri (passwordVisible ve setPasswordVisible artık tanımlı)
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [passwordConfirmVisible, setPasswordConfirmVisible] = useState(false);

    // Modal ve Checkbox State'leri
    const [showKvkkModal, setShowKvkkModal] = useState(false);
    const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
    const [kvkkIsChecked, setKvkkIsChecked] = useState(false);
    const [disclaimerIsChecked, setDisclaimerIsChecked] = useState(false);
    const [kvkkAcceptedAt, setKvkkAcceptedAt] = useState(null);
    const [disclaimerAcceptedAt, setDisclaimerAcceptedAt] = useState(null);
    const [kvkkScrolledToBottom, setKvkkScrolledToBottom] = useState(false);
    const [disclaimerScrolledToBottom, setDisclaimerScrolledToBottom] = useState(false);
    const kvkkScrollRef = useRef(null);
    const disclaimerScrollRef = useRef(null);

    // Form fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [emailVal, setEmailVal] = useState('');
    const [passwordVal, setPasswordVal] = useState('');
    // Dynamic Policies State
    const [kvkkContent, setKvkkContent] = useState(null);
    const [disclaimerContent, setDisclaimerContent] = useState(null);
    const [kvkkPolicyMeta, setKvkkPolicyMeta] = useState(null);
    const [disclaimerPolicyMeta, setDisclaimerPolicyMeta] = useState(null);

    useEffect(() => {
        // Fetch Policies
        const fetchPolicies = async () => {
            try {
                // Fetch KVKK
                fetch(`${API_BASE_URL}/api/policies/kvkk`)
                    .then(res => res.json())
                    .then(data => {
                        if (data.content) setKvkkContent(data.content);
                        setKvkkPolicyMeta(data || null);
                    })
                    .catch(err => console.error('KVKK fetch err', err));

                // Fetch Terms (Disclaimer)
                fetch(`${API_BASE_URL}/api/policies/terms`)
                    .then(res => res.json())
                    .then(data => {
                        if (data.content) setDisclaimerContent(data.content);
                        setDisclaimerPolicyMeta(data || null);
                    })
                    .catch(err => console.error('Terms fetch err', err));
            } catch (error) {
                console.error('Policy fetch error', error);
            }
        };
        fetchPolicies();
    }, []);

    const isScrolledToBottom = (element) => {
        if (!element) return false;
        const threshold = 12;
        return element.scrollTop + element.clientHeight >= element.scrollHeight - threshold;
    };

    const updateKvkkScrollState = () => {
        setKvkkScrolledToBottom(isScrolledToBottom(kvkkScrollRef.current));
    };

    const updateDisclaimerScrollState = () => {
        setDisclaimerScrolledToBottom(isScrolledToBottom(disclaimerScrollRef.current));
    };

    const showReadWarning = () => {
        alert('Lütfen devam etmeden önce metnin tamamını aşağı kaydırarak okuyun.');
    };

    const handleKvkkApprove = () => {
        if (!kvkkScrolledToBottom) {
            showReadWarning();
            return;
        }
        setKvkkIsChecked(true);
        setKvkkAcceptedAt(new Date().toISOString());
        setShowKvkkModal(false);
    };

    const handleDisclaimerApprove = () => {
        if (!disclaimerScrolledToBottom) {
            showReadWarning();
            return;
        }
        setDisclaimerIsChecked(true);
        setDisclaimerAcceptedAt(new Date().toISOString());
        setShowDisclaimerModal(false);
    };

    useEffect(() => {
        if (!showKvkkModal) {
            setKvkkScrolledToBottom(false);
            return;
        }
        const timer = setTimeout(updateKvkkScrollState, 0);
        return () => clearTimeout(timer);
    }, [showKvkkModal, kvkkContent]);

    useEffect(() => {
        if (!showDisclaimerModal) {
            setDisclaimerScrolledToBottom(false);
            return;
        }
        const timer = setTimeout(updateDisclaimerScrollState, 0);
        return () => clearTimeout(timer);
    }, [showDisclaimerModal, disclaimerContent]);
    const [passwordConfirmVal, setPasswordConfirmVal] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Form Gönderme İşlemi
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!kvkkIsChecked || !disclaimerIsChecked) {
            alert("Lütfen tüm zorunlu beyanları okuyup onaylayınız.");
            return;
        }
        if (!emailVal || !passwordVal) {
            alert('E-posta ve şifre gerekli');
            return;
        }
        if (passwordVal !== passwordConfirmVal) {
            alert('Şifreler eşleşmiyor');
            return;
        }
        const htmlToText = (html) => {
            if (!html) return '';
            const temp = document.createElement('div');
            temp.innerHTML = html;
            return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
        };

        const buildConsentPayload = ({ key, title, content, acceptedAt, scrolledToEnd, policyMeta }) => ({
            key,
            title,
            accepted: true,
            acceptedAt: acceptedAt || new Date().toISOString(),
            scrolledToEnd: Boolean(scrolledToEnd),
            source: 'signup_modal',
            version: policyMeta?.updatedAt ? `${key}_${policyMeta.updatedAt}` : `${key}_legacy_v1`,
            policyUpdatedAt: policyMeta?.updatedAt || null,
            contentText: htmlToText(content).slice(0, 10000),
            locale: 'tr-TR'
        });

        setSubmitting(true);
        try {
            const name = `${firstName.trim()} ${lastName.trim()}`.trim();
            const signupUrl = `${API_BASE_URL}/auth/signup`;
            const consents = [
                buildConsentPayload({
                    key: 'kvkk',
                    title: kvkkPolicyMeta?.title || 'KVKK Aydınlatma Metni',
                    content: kvkkContent,
                    acceptedAt: kvkkAcceptedAt,
                    scrolledToEnd: kvkkScrolledToBottom,
                    policyMeta: kvkkPolicyMeta
                }),
                buildConsentPayload({
                    key: 'terms',
                    title: disclaimerPolicyMeta?.title || 'Sorumluluk Reddi Beyanı',
                    content: disclaimerContent,
                    acceptedAt: disclaimerAcceptedAt,
                    scrolledToEnd: disclaimerScrolledToBottom,
                    policyMeta: disclaimerPolicyMeta
                })
            ];
            const res = await fetch(signupUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-client-install-id': getClientInstallId() },
                credentials: 'include',
                body: JSON.stringify({
                    email: emailVal,
                    password: passwordVal,
                    name,
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    consents
                })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || err.message || 'signup_failed');
            }
            const data = await res.json().catch(() => ({}));
            if (data && data.token) {
                localStorage.setItem('authToken', data.token);
            }
            // on success, check survey and redirect accordingly
            try {
                const profileUrl = `${API_BASE_URL}/api/profile`;
                const authToken = localStorage.getItem('authToken');
                const pr = await fetch(profileUrl, {
                    credentials: 'include',
                    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
                });
                if (pr.ok) {
                    const profile = await pr.json();
                    const intake = profile && profile.intakeSurvey;
                    const completed = intake && intake.completedRegistration === true;
                    if (completed) navigate('/dashboard'); else navigate('/registration-survey');
                } else {
                    navigate('/registration-survey');
                }
            } catch (e) {
                navigate('/registration-survey');
            }
        } catch (err) {
            console.error('Signup error', err);
            alert('Kayıt sırasında hata oluştu: ' + (err.message || 'Hata'));
        } finally {
            setSubmitting(false);
        }
    };

    // Dark Mode uyumu için Tailwind class'ları kullanılmıştır.
    return (
        // Sayfa Arka Planı (Dark Mode: bg-[#1a0a0a] - Koyu Kırmızımsı Dark)
        <div className="min-h-screen flex flex-col justify-center items-center p-4 relative bg-white dark:bg-[#1a1a1a]">

            {/* Dark Mode Toggle Butonu (Sağ Üstte Sabit) */}
            <button
                className="fixed top-5 right-5 flex items-center justify-center size-10 rounded-full hover:bg-gray-100 dark:hover:bg-white/10  text-gray-500 dark:text-gray-300 z-50"
                type="button"
                onClick={toggleDarkMode} // App.js'ten gelen toggle fonksiyonunu kullanır
                aria-label="Temayı değiştir"
            >
                <span className="material-symbols-outlined">
                    {isDarkMode ? 'dark_mode' : 'light_mode'}
                </span>
            </button>


            {/* Background Decoration (Dark Mode'da hafif bulanık efekt) */}
            <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden hidden dark:block">
                <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-primary/5 blur-[120px] rounded-full"></div>
                <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] bg-primary/5 blur-[120px] rounded-full"></div>
            </div>

            {/* Main Card Container */}
            <div className="relative z-10 w-full max-w-[480px] flex flex-col gap-6 pt-10 pb-16">
                {/* 1. Header Section (Dark/Light uyumlu metin renkleri) */}
                <div className="flex flex-col items-center gap-4">
                    <div
                        className="bg-center bg-no-repeat bg-cover rounded-full h-24 w-24 shadow-lg ring-4 ring-gray-100 dark:ring-[#2a1515]"
                        data-alt="Abstract minimalist chef hat logo with steam on dark background"
                        style={{ backgroundImage: `url(${Logo})` }}
                    ></div>
                    <div className="flex flex-col items-center text-center">
                        <h1 className="text-gray-900 dark:text-white text-2xl font-bold tracking-tight">Bana Bir Tarif Ver</h1>
                        <p className="text-gray-500 dark:text-gray-400 text-sm font-normal mt-1">Bugün ne yemek yapsam?</p>
                    </div>
                </div>

                {/* 2. Segmented Buttons (Tabs) (Dark/Light uyumlu yüzey rengi) */}
                <div className="bg-gray-100 dark:bg-[#2a1515] p-1 rounded-xl flex shadow-sm border border-gray-200 dark:border-gray-800">
                    <Link to="/giris-yap" className="flex-1 text-center">
                        <div className="flex items-center justify-center py-2.5 px-4 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-400 transition-all hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5">
                            Giriş Yap
                        </div>
                    </Link>
                    <Link to="/kayit-ol" aria-current="page" className="flex-1 text-center">
                        <div className="flex items-center justify-center py-2.5 px-4 rounded-lg text-sm font-medium bg-primary text-white shadow-md">
                            Kayıt Ol
                        </div>
                    </Link>
                </div>

                {/* 3. Form Section */}
                <form className="flex flex-col gap-5" onSubmit={handleSubmit}>

                    {/* Email Input */}
                    <label className="flex flex-col gap-1.5">
                        <span className="text-gray-900 dark:text-white text-sm font-medium">Ad</span>
                        <div className="relative flex items-center">
                            <input
                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3.5 pl-4 pr-12 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                                placeholder="Adınız"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                            />
                        </div>
                    </label>

                    <label className="flex flex-col gap-1.5">
                        <span className="text-gray-900 dark:text-white text-sm font-medium">Soyad</span>
                        <div className="relative flex items-center">
                            <input
                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3.5 pl-4 pr-12 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                                placeholder="Soyadınız"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                required
                            />
                        </div>
                    </label>

                    <label className="flex flex-col gap-1.5">
                        <span className="text-gray-900 dark:text-white text-sm font-medium">E-posta Adresi</span>
                        <div className="relative flex items-center">
                            <input
                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3.5 pl-4 pr-12 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                                placeholder="ornek@email.com"
                                type="email"
                                value={emailVal}
                                onChange={(e) => setEmailVal(e.target.value)}
                                required
                            />
                            <span className="material-symbols-outlined absolute right-4 text-gray-500 dark:text-gray-400 pointer-events-none">mail</span>
                        </div>
                    </label>

                    {/* Password Input */}
                    <label className="flex flex-col gap-1.5">
                        <span className="text-gray-900 dark:text-white text-sm font-medium">Şifre</span>
                        <div className="relative flex items-center">
                            <input
                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3.5 pl-4 pr-12 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                                type={passwordVisible ? 'text' : 'password'}
                                value={passwordVal}
                                onChange={(e) => setPasswordVal(e.target.value)}
                                required
                            />
                            <button
                                className="absolute right-4 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center justify-center"
                                type="button"
                                onClick={() => setPasswordVisible(p => !p)}
                            >
                                <span className="material-symbols-outlined">{passwordVisible ? 'visibility' : 'visibility_off'}</span>
                            </button>
                        </div>
                    </label>

                    {/* Password Confirm Input */}
                    <label className="flex flex-col gap-1.5">
                        <span className="text-gray-900 dark:text-white text-sm font-medium">Şifre Tekrar</span>
                        <div className="relative flex items-center">
                            <input
                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3.5 pl-4 pr-12 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                                type={passwordConfirmVisible ? 'text' : 'password'}
                                value={passwordConfirmVal}
                                onChange={(e) => setPasswordConfirmVal(e.target.value)}
                                required
                            />
                            <button
                                className="absolute right-4 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center justify-center"
                                type="button"
                                onClick={() => setPasswordConfirmVisible(p => !p)}
                            >
                                <span className="material-symbols-outlined">{passwordConfirmVisible ? 'visibility' : 'visibility_off'}</span>
                            </button>
                        </div>
                    </label>

                    {/* Legal Consents (Modal Tetikleyicileri) */}
                    <div className="flex flex-col gap-3 mt-1">

                        {/* KVKK Checkbox */}
                        <div className="flex items-start gap-3">
                            <div className="relative flex items-center h-5">
                                <input
                                    className="peer h-5 w-5 rounded border-gray-400 dark:border-gray-700 bg-white dark:bg-gray-800 text-primary focus:ring-0 focus:ring-offset-0 cursor-pointer"
                                    id="kvkk-check"
                                    type="checkbox"
                                    checked={kvkkIsChecked}
                                    readOnly
                                    onClick={() => setShowKvkkModal(true)}
                                />
                            </div>
                            <div className="text-sm leading-5">
                                <span className="text-gray-500 dark:text-gray-400">Hesap oluşturarak, </span>
                                <span
                                    className="text-primary hover:text-primary-dark underline cursor-pointer font-medium"
                                    onClick={() => setShowKvkkModal(true)}
                                >
                                    KVKK Aydınlatma Metnini
                                </span>
                                <span className="text-gray-500 dark:text-gray-400"> okudum ve onaylıyorum.</span>
                            </div>
                        </div>

                        {/* Disclaimer Checkbox */}
                        <div className="flex items-start gap-3">
                            <div className="relative flex items-center h-5">
                                <input
                                    className="peer h-5 w-5 rounded border-gray-400 dark:border-gray-700 bg-white dark:bg-gray-800 text-primary focus:ring-0 focus:ring-offset-0 cursor-pointer"
                                    id="disclaimer-check"
                                    type="checkbox"
                                    checked={disclaimerIsChecked}
                                    readOnly
                                    onClick={() => setShowDisclaimerModal(true)}
                                />
                            </div>
                            <div className="text-sm leading-5">
                                <span
                                    className="text-primary hover:text-primary-dark underline cursor-pointer font-medium"
                                    onClick={() => setShowDisclaimerModal(true)}
                                >
                                    Sorumluluk Reddi Beyanını
                                </span>
                                <span className="text-gray-500 dark:text-gray-400"> okudum ve kabul ediyorum.</span>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        className="mt-2 w-full bg-primary hover:bg-primary-dark text-white font-bold py-3.5 px-4 rounded-lg shadow-lg shadow-red-900/20 transition-all active:scale-[0.98] disabled:opacity-60"
                        type="submit"
                        disabled={submitting}
                    >
                        {submitting ? 'Kayıt oluyor...' : 'Kayıt Ol'}
                    </button>

                    {/* VEYA Yazısı */}
                    <div className="relative flex py-2 items-center">
                        <div className="flex-grow border-t border-gray-200 dark:border-gray-700"></div>
                        <span className="flex-shrink-0 mx-4 text-gray-500 dark:text-gray-400 text-xs uppercase">veya</span>
                        <div className="flex-grow border-t border-gray-200 dark:border-gray-700"></div>
                    </div>

                    {/* Google Button */}
                    <button onClick={() => { window.location.href = `${API_BASE_URL}/auth/google`; }} className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-medium py-3.5 px-4 rounded-lg flex items-center justify-center gap-3 transition-colors" type="button">
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"></path>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
                        </svg>
                        Google İle Devam Et
                    </button>
                </form>

                {/* En Alttaki Giriş Yap Yazısı */}
                <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Zaten bir hesabın var mı? <Link className="text-primary hover:underline" to="/giris-yap">Giriş Yap</Link>
                </p>
            </div>

            {/* ========================================
                MODALS
                ========================================
            */}

            {/* KVKK Modal Yapısı */}
            {showKvkkModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) { setShowKvkkModal(false); } }}
                >
                    <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 w-full max-w-[calc(100vw-2rem)] sm:max-w-lg rounded-xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-800">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">KVKK Aydınlatma Metni</h3>
                            <button className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white p-1" onClick={() => setShowKvkkModal(false)}>
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div
                            ref={kvkkScrollRef}
                            onScroll={updateKvkkScrollState}
                            className="bbtv-rich-text p-6 overflow-y-auto overflow-x-hidden custom-scrollbar flex-1 text-gray-700 dark:text-white/80 text-sm leading-relaxed space-y-4"
                        >
                            {kvkkContent ? (
                                <div className="bbtv-rich-text" dangerouslySetInnerHTML={{ __html: kvkkContent }} />
                            ) : (
                                <>
                                    <p><strong className="text-gray-900 dark:text-white">1. Veri Sorumlusu</strong><br />
                                        6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") uyarınca, kişisel verileriniz; veri sorumlusu olarak "Bana Bir Tarif Ver" platformu tarafından aşağıda açıklanan kapsamda işlenebilecektir.</p>
                                    <p><strong className="text-gray-900 dark:text-white">2. İşlenme Amacı</strong><br />
                                        Platform üyelik işlemlerinin gerçekleştirilmesi, hizmetlerin sunulması ve yasal yükümlülüklerin yerine getirilmesi amaçlarıyla işlenmektedir.</p>
                                    <p><strong className="text-gray-900 dark:text-white">3. İşlenen Veriler</strong><br />
                                        Kimlik Bilgileri, İletişim Bilgileri (E-posta adresi), İşlem Güvenliği Bilgileri (IP adresi, şifre).</p>
                                </>
                            )}
                            <div className="h-10"></div>
                        </div>
                        <div className="p-5 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#201010]/50 rounded-b-xl flex justify-end gap-3">
                            <button className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 cursor-pointer text-sm font-medium transition-colors" onClick={() => setShowKvkkModal(false)}>Vazgeç</button>
                            <button
                                className={`px-6 py-2 rounded-lg text-sm font-bold transition-colors ${kvkkScrolledToBottom
                                    ? 'bg-primary text-white hover:bg-primary-dark cursor-pointer shadow-lg shadow-red-900/20'
                                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-300 cursor-not-allowed'
                                    }`}
                                type="button"
                                aria-disabled={!kvkkScrolledToBottom}
                                onClick={handleKvkkApprove}
                            >
                                Okudum, Onaylıyorum
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Disclaimer Modal Yapısı */}
            {showDisclaimerModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) { setShowDisclaimerModal(false); } }}
                >
                    <div className="bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 w-full max-w-[calc(100vw-2rem)] sm:max-w-lg rounded-xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-800">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Sorumluluk Reddi Beyanı</h3>
                            <button className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white p-1" onClick={() => setShowDisclaimerModal(false)}>
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div
                            ref={disclaimerScrollRef}
                            onScroll={updateDisclaimerScrollState}
                            className="bbtv-rich-text p-6 overflow-y-auto overflow-x-hidden custom-scrollbar flex-1 text-gray-700 dark:text-white/80 text-sm leading-relaxed space-y-4"
                        >
                            {disclaimerContent ? (
                                <div className="bbtv-rich-text" dangerouslySetInnerHTML={{ __html: disclaimerContent }} />
                            ) : (
                                <>
                                    <p><strong className="text-gray-900 dark:text-white">1. Genel Bilgilendirme</strong><br />
                                        Sunulan tüm tarifler ve öneriler yalnızca genel bilgilendirme amaçlıdır. Tıbbi tavsiye niteliği taşımaz.</p>
                                    <p><strong className="text-gray-900 dark:text-white">2. Alerjen Uyarısı</strong><br />
                                        Kullanıcıların kendi sağlık durumlarına ve alerjilerine uygun tarifleri seçmesi ve malzemeleri kontrol etmesi kendi sorumluluğundadır. Uygulama, alerjik reaksiyonlardan sorumlu tutulamaz.</p>
                                </>
                            )}
                            <div className="h-10"></div>
                        </div>
                        <div className="p-5 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#201010]/50 rounded-b-xl flex justify-end gap-3">
                            <button className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 cursor-pointer text-sm font-medium transition-colors" onClick={() => setShowDisclaimerModal(false)}>Vazgeç</button>
                            <button
                                className={`px-6 py-2 rounded-lg text-sm font-bold transition-colors ${disclaimerScrolledToBottom
                                    ? 'bg-primary text-white hover:bg-primary-dark cursor-pointer shadow-lg shadow-red-900/20'
                                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-300 cursor-not-allowed'
                                    }`}
                                type="button"
                                aria-disabled={!disclaimerScrolledToBottom}
                                onClick={handleDisclaimerApprove}
                            >
                                Okudum, Kabul Ediyorum
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignupPage;
